import { DailyActivityPanel } from "@/components/DailyActivityPanel";

import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AuthImage } from "@/components/AuthImage";
import { fmtDate } from "@/components/ui-kit";
import { Bus, Loader2 } from "lucide-react";

export const initials = (n) => (n || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");

export function RoundAvatar({ name, photo, size = "h-14 w-14 text-base" }) {
  if (photo) return <AuthImage src={photo} alt={name} className={`${size} rounded-full object-cover ring-2 ring-white shadow`} />;
  return <div className={`${size} rounded-full bg-gradient-to-br from-violet-500 to-purple-700 flex items-center justify-center text-white font-bold ring-2 ring-white shadow`}>{initials(name)}</div>;
}

const pctTone = (p) => (p == null ? "text-slate-400" : p >= 90 ? "text-emerald-600" : p >= 75 ? "text-amber-600" : "text-rose-600");

export function StudentCard({ s, onOpen, showSection = true }) {
  const a = s.attendance || {};
  return (
    <button data-testid={`student-card-${s.admission_number}`} onClick={() => onOpen(s)}
      className="text-left bg-white rounded-2xl border border-violet-100 p-4 hover:border-violet-400 hover:shadow-md transition-[border-color,box-shadow] duration-200">
      <div className="flex items-center gap-3">
        <div className="relative">
          <RoundAvatar name={s.name} photo={s.photo_url} />
          {s.uses_transport && <span data-testid={`student-bus-${s.admission_number}`} title="Uses school transport" className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-amber-400 text-white flex items-center justify-center ring-2 ring-white"><Bus size={13} /></span>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-slate-900 truncate">{s.name}</div>
          <div className="text-xs text-slate-500 font-mono">{s.admission_number}</div>
          <div className="flex gap-1.5 mt-1 flex-wrap">
            <Badge className="bg-violet-100 text-violet-800 hover:bg-violet-100 font-medium">{s.class_name}</Badge>
            {showSection && <Badge variant="outline" className="border-slate-200 text-slate-600">Sec {s.section}</Badge>}
          </div>
        </div>
        <div className="text-right">
          <div data-testid={`student-att-pct-${s.admission_number}`} className={`font-display text-2xl font-extrabold ${pctTone(a.pct)}`}>{a.pct == null ? "—" : `${a.pct}%`}</div>
          <div className="text-[10px] uppercase tracking-wide text-slate-400">attendance</div>
        </div>
      </div>
      {s.contact_teacher_name && <div data-testid={`student-contact-${s.admission_number}`} className="text-[11px] text-violet-700 mt-2 truncate">Contact teacher: {s.contact_teacher_name}</div>}
      <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-slate-100 text-center">
        <div><div className="font-bold text-slate-800">{a.total ?? 0}</div><div className="text-[10px] text-slate-500">Total days</div></div>
        <div><div className="font-bold text-emerald-600">{a.present ?? 0}</div><div className="text-[10px] text-slate-500">Present</div></div>
        <div><div className="font-bold text-rose-600">{a.absent ?? 0}</div><div className="text-[10px] text-slate-500">Absent</div></div>
      </div>
    </button>
  );
}

const ST = { Present: "bg-emerald-100 text-emerald-800", Absent: "bg-rose-100 text-rose-700", Late: "bg-amber-100 text-amber-800", Leave: "bg-sky-100 text-sky-800" };

function AttendanceHistory({ id }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get(`/students/${id}/attendance-history`).then((r) => setRows(r.data)).catch(() => setRows([])); }, [id]);
  if (!rows) return <Loader2 className="animate-spin text-violet-500 mx-auto my-6" />;
  if (!rows.length) return <div className="text-sm text-slate-500 py-4">No attendance recorded yet.</div>;
  return (
    <div data-testid="attendance-history" className="max-h-72 overflow-y-auto divide-y divide-slate-100">
      {rows.map((r) => <div key={r.date} className="flex items-center justify-between py-2 text-sm"><span>{fmtDate(r.date)}</span><Badge className={ST[r.status]}>{r.status}</Badge></div>)}
    </div>
  );
}

function groupByTerm(items, termKey = "term") {
  const g = {};
  items.forEach((x) => { (g[x[termKey]] = g[x[termKey]] || []).push(x); });
  return Object.entries(g);
}

function Academics({ id }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/students/${id}/academics`).then((r) => setD(r.data)).catch(() => setD({ stage: "", grades: [], reports: [] })); }, [id]);
  if (!d) return <Loader2 className="animate-spin text-violet-500 mx-auto my-6" />;
  const play = d.stage === "playschool";
  const reportTerms = d.reports.map((r) => [`${r.term} • ${r.academic_year}`, r]);
  if (!d.grades.length && !reportTerms.length) return <div className="text-sm text-slate-500 py-4">No {play ? "assessments" : "marks"} recorded yet.</div>;
  return (
    <div data-testid={play ? "academics-playschool" : "academics-elementary"} className="max-h-80 overflow-y-auto space-y-4">
      <Badge className={play ? "bg-pink-100 text-pink-800" : "bg-violet-100 text-violet-800"}>{play ? "Playschool • activity-based assessment" : "Elementary • subject marks & grades"}</Badge>
      {!play && groupByTerm(d.grades).map(([term, rows]) => (
        <div key={term}><div className="text-xs font-bold text-violet-700 uppercase mb-1">{term} • Gradebook</div>
          <table className="w-full text-sm"><thead><tr className="text-left text-xs text-slate-500"><th>Subject</th><th>Marks</th><th>Grade</th></tr></thead>
            <tbody>{rows.map((g) => <tr key={g.id} className="border-t border-slate-100"><td className="py-1.5">{g.subject}</td><td>{g.marks}/{g.max_marks}</td><td className="font-semibold">{g.grade}</td></tr>)}</tbody></table></div>
      ))}
      {reportTerms.map(([label, r]) => (
        <div key={r.id}><div className="text-xs font-bold text-violet-700 uppercase mb-1">{label} • {play ? "Activity assessment" : "Progress report"}</div>
          <table className="w-full text-sm"><thead><tr className="text-left text-xs text-slate-500"><th>{play ? "Activity area" : "Subject"}</th>{!play && <th>Marks</th>}<th>{play ? "Level" : "Grade"}</th><th>Remarks</th></tr></thead>
            <tbody>{r.subjects.map((x, i) => <tr key={i} className="border-t border-slate-100"><td className="py-1.5">{x.subject}</td>{!play && <td>{x.marks || "—"}</td>}<td className="font-semibold">{x.grade || "—"}</td><td className="text-slate-500">{x.remarks}</td></tr>)}</tbody></table>
          {r.overall_remarks && <div className="text-xs text-slate-600 mt-1">{r.overall_remarks}</div>}</div>
      ))}
    </div>
  );
}

export function ProfileTabs({ student, details }) {
  return (
    <Tabs defaultValue="details" className="mt-5">
      <TabsList className="bg-violet-50">
        <TabsTrigger value="details" data-testid="profile-tab-details">Details</TabsTrigger>
        <TabsTrigger value="activity" data-testid="profile-tab-activity">Daily Activity</TabsTrigger>
        <TabsTrigger value="attendance" data-testid="profile-tab-attendance">Attendance</TabsTrigger>
        <TabsTrigger value="academics" data-testid="profile-tab-academics">Academics</TabsTrigger>
      </TabsList>
      <TabsContent value="details">{details}</TabsContent>
      <TabsContent value="activity" className="mt-3"><DailyActivityPanel student={student} /></TabsContent>
      <TabsContent value="attendance">
        {student.attendance && <div className="grid grid-cols-4 gap-2 text-center mb-3 text-sm">
          {[["Total", student.attendance.total, "text-slate-800"], ["Present", student.attendance.present, "text-emerald-600"], ["Absent", student.attendance.absent, "text-rose-600"], ["Rate", student.attendance.pct == null ? "—" : `${student.attendance.pct}%`, "text-violet-700"]].map(([l, v, c]) => (
            <div key={l} className="bg-slate-50 rounded-xl py-2"><div className={`font-bold ${c}`}>{v}</div><div className="text-[10px] text-slate-500">{l}</div></div>))}
        </div>}
        <AttendanceHistory id={student.id} />
      </TabsContent>
      <TabsContent value="academics"><Academics id={student.id} /></TabsContent>
    </Tabs>
  );
}
